import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SchKService } from './f1120-sch-k.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120-sch-k',
    templateUrl: './f1120-sch-k.component.html',
    styleUrls: ['./f1120-sch-k.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchKComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // Component properties migrated from F1120ScheduleKCtrl.js
  modalTitle = 'Schedule K Other Information';
  originalData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  isSaveClicked = false;
  // Guard to prevent redundant init calls (init runs on the direct ngOnInit
  // call plus every setTabActive / globalParamsChanged emission).
  private initialized = false;
  
  // Entity information
  entityList: any[] = [];

  // Form data structures (maintaining AngularJS structure)
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  
  scheduleKFormView: { rows: any[] } = { rows: [] };
  saveParamDtls: any[] = [];
  mainData: any[] = [];

  // Countries and states data
  allCountries: any[] = [];
  allUSStates: any[] = [];
  USCountry_index: number = 0;

  // Entity lists for sub-tables
  entitiesData: any[] = [];
  individualsList: any[] = [];
  corporationList: any[] = [];
  otherEntityList: any[] = [];

  // Code master and EIN data
  codeMastMap: any[] = [];
  einList: any[] = [];
  einErrorList: any[] = [];
  validEIN: boolean = true;

  // Tab configuration (maintaining AngularJS structure)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;
  currentTabName = "Form Entry";
  
  // Modal minimization state
  minimise = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchKService: F1120SchKService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Render after async data arrives. Inside an Angular custom element (OnPush)
   * the first modal render was not flushing, so the form stayed empty until
   * reopened. Several fetches resolve in parallel; deferring to a macrotask
   * lets them settle and the view fully initialize before we detect changes,
   * then a synchronous detectChanges() paints it. Guarded so a response that
   * arrives after the modal closed cannot throw.
   */
  private refreshView(): void {
    setTimeout(() => {
      try {
        this.cdr.detectChanges();
      } catch {
        /* view already destroyed (modal closed) */
      }
    });
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {}
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.initialized) {
        return;
      }
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';

      // 🔴 CRITICAL: Only proceed if we have row data - NO MOCKS!
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.initialized = true;
        this.setupEntityData();
        this.getcodeMastDetails();
        this.initSchK();
      }
    } catch (e) {
      console.error('Error in F1120SchK init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  // Initialize Schedule K form
  initSchK() {
    this.fetchUSStateList();
    this.fetchCountryList().then(() => {
      this.fetchSubTableRecords();
      this.fetchSchKrecords();
      this.fetchEINList();
    });
  }

  // Fetch countries list
  fetchCountryList(): Promise<any> {
    const params = {};

    return firstValueFrom(
      this.jsonObjectFactory.getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=kgdgoh',
        params,
        this.globalService,
        null,
        null
      )
    )
      .then((data: any) => {
      this.allCountries = [];
      
      data.jsonObject.forEach((country: any, index: number) => {
        if (country.COUNTRY_CODE === "US") {
          this.USCountry_index = index;
        }
        this.allCountries.push({
          "id": country.COUNTRY_CODE,
          "label": country.COUNTRY_DESC
        });
      });
      this.refreshView();
    });
  }

  // Fetch US states list
  fetchUSStateList() {
    const params = {};
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=yh4p2z',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.allUSStates = [];
      data.jsonObject.forEach((state: any) => {
        this.allUSStates.push({
          "id": state.STATE_CODE,
          "label": state.STATE_DESC
        });
      });
      this.refreshView();
    });
  }

  // Fetch EIN list
  fetchEINList() {
    const params = { 
      "action_code": '0jdhck', 
      "sso_id": this.logged_in_user, 
      "tax_year": this.rowData.TAX_YEAR, 
      "scenario": this.rowData.SCENARIO, 
      "jcd_key": this.rowData.JCD_KEY, 
      "form_no": 'F1120_K_EIN' 
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.einList = data.jsonObject;
      this.refreshView();
    });
  }

  // Get code master details for dropdown
  getcodeMastDetails() {
    const params = {
      "action_code": 'kdep1i',
      "sso_id": this.logged_in_user,
      "tax_year": this.rowData.TAX_YEAR,
      "jcd_key": this.rowData.JCD_KEY,
      "scenario": this.rowData.SCENARIO
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data.callSuccess === "1") {
        this.codeMastMap = data.jsonObject;
      } else {
        if (data.errorMessage === "no access") {
          this.gtwSnackbar.openSnackBar("Sorry you do not have access to do the requested action.", 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar("An error has occurred, If you continue to experience this issue please visit the help page to contact support.", 'gtw-snackbar--red', true, 4);
        }
      }
      this.refreshView();
    });
  }

  // Fetch sub-table records
  fetchSubTableRecords() {
    const params = {
      combination_key: this.rowData.COMBINATION_KEY,
      schedule: "K"
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=2xuiy5',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      // Reset sub-table lists to avoid accumulating duplicates when init() runs
      // more than once (e.g. on setTabActive / globalParamsChanged emissions).
      this.entitiesData = [];
      this.individualsList = [];
      this.corporationList = [];
      this.otherEntityList = [];

      data.jsonObject.forEach((row: any) => {
        if (row.LINE_NO === '4a1') {
          row.DATA_ATTRIB_D = this.findCountryID(row.DATA_ATTRIB_D);
          row.DATA_ATTRIB_E = parseFloat(row.DATA_ATTRIB_E);
          this.entitiesData.push(row);
        } else if (row.LINE_NO === '4b1') {
          row.DATA_ATTRIB_C = this.findCountryID(row.DATA_ATTRIB_C);
          row.DATA_ATTRIB_D = parseFloat(row.DATA_ATTRIB_D);
          this.individualsList.push(row);
        } else if (row.LINE_NO === '5a1') {
          row.DATA_ATTRIB_C = this.findCountryID(row.DATA_ATTRIB_C);
          row.DATA_ATTRIB_D = parseFloat(row.DATA_ATTRIB_D);
          this.corporationList.push(row);
        } else {
          row.DATA_ATTRIB_C = this.findCountryID(row.DATA_ATTRIB_C);
          row.DATA_ATTRIB_D = parseFloat(row.DATA_ATTRIB_D);
          this.otherEntityList.push(row);
        }
      });
      this.refreshView();
    });
  }

  // Find country by ID - returns the country ID string for select ngModel
  findCountryID(temp: string): string {
    for (let i = 0; i < this.allCountries.length; i++) {
      if (this.allCountries[i].id === temp) {
        return this.allCountries[i].id;  // Return just the ID string
      }
    }
    return temp;  // Return original value if not found
  }

  // 🔴 CRITICAL: API calls use REAL rowData - NO globalParams or 'BASE'!
  fetchSchKrecords() {
    const params = { 
      "action_id": 31333, 
      "tax_year": this.rowData.TAX_YEAR,           // NOT hardcoded year
      "scenario": this.rowData.SCENARIO,           // NOT 'BASE'
      "jcd_key": this.rowData.JCD_KEY,            // NOT globalParams.jcd_key
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_name": "F1120", 
      "schedule_name": "K", 
      "part_no": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.refreshView();
    });
  }

  private processFormData(data: any) {
    const scheduleKForm: { rows: any[] } = { rows: [] };
    let saveParamDtls: any[] = [];
    let tempDataObj: any = {};

    for (let i = 1; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = data.irsForm.formItemList[i].dataRowList[0].dataCellList.length;
      
      tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[0].name;
      tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[1].name;
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      
      if (rows > 2) {
        this.iterateArray(rows, data.irsForm.formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      
      scheduleKForm.rows.push(tempDataObj);
    }

    // Apply code master mapping
    if (this.codeMastMap.length > 1) {
      this.codeMastMap.forEach((obj: any) => {
        if (scheduleKForm.rows[5]?.value === obj.CODE_NAME) {
          scheduleKForm.rows[5].value = obj.CODE_NAME;
        }
      });
    }

    this.originalData = _.cloneDeep(scheduleKForm.rows);
    this.scheduleKFormView = scheduleKForm;
    this.cdr.markForCheck();
  }

  // Process different column counts in form data
  private iterateArray(count: number, array: any[], tempDataObj: any) {
    if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      
      if (tempDataObj.num === "2c") {
        tempDataObj.value_user_input = array[2].value;
      }
      
      // Handle conditional fields based on line numbers
      this.handleConditionalFields(tempDataObj, array[2].value);
      
    } else if (count === 4) {
      this.handleFourColumnData(tempDataObj, array);
    } else {
      this.handleMultiColumnData(tempDataObj, array);
    }
  }

  private handleConditionalFields(tempDataObj: any, value: any) {
    // Line 3 conditional logic
    if (tempDataObj.num === "3a" || tempDataObj.num === "3b" || tempDataObj.num === '3c') {
      const line3Value = this.scheduleKFormView.rows.find(o => o.num === '3');
      if (String(line3Value?.value) !== "true") {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = true;
      } else {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = false;
      }
    }
    
    // Line 7 conditional logic
    if (tempDataObj.num === "7a" || tempDataObj.num === "7b" || tempDataObj.num === '7c') {
      const line7Value = this.scheduleKFormView.rows.find(o => o.num === '7');
      if (String(line7Value?.value) !== "true") {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = true;
      } else {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = false;
      }
    }
    
    // Line 26 conditional logic
    if (tempDataObj.num === "26a" || tempDataObj.num === "26b") {
      const line26Value = this.scheduleKFormView.rows.find(o => o.num === '26');
      if (String(line26Value?.value) !== "true") {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = true;
      } else {
        tempDataObj.value_user_input = value;
        tempDataObj.conditionalField = false;
      }
    }

    // Handle different value types
    this.processValueTypes(tempDataObj, value);
  }

  private processValueTypes(tempDataObj: any, value: any) {
    if (value == null) {
      if (tempDataObj.num === "1a" || tempDataObj.num === "1b" || tempDataObj.num === '8' || tempDataObj.num === '11') {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = '';
      }
    } else if (value === "true") {
      if (tempDataObj.num === "1a" || tempDataObj.num === "1b" || tempDataObj.num === '8' || tempDataObj.num === '11') {
        tempDataObj.value = true;
      } else {
        tempDataObj.value = '';
      }
    } else if (value === "false" || value === "0" || value === 'NA') {
      if (tempDataObj.num === "1a" || tempDataObj.num === "1b" || tempDataObj.num === '8' || tempDataObj.num === '11') {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = '';
      }
    } else {
      tempDataObj.value = value;
    }
  }

  private handleFourColumnData(tempDataObj: any, array: any[]) {
    if (tempDataObj.num === "1c") {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.value = array[2].value;
      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];
      tempDataObj.value_user_input = array[3].value;
      
      if (tempDataObj.value == null || tempDataObj.value === "false" || tempDataObj.value === "0") {
        tempDataObj.value = false;
        tempDataObj.conditionalField = true;
      } else if (tempDataObj.value === "true") {
        tempDataObj.value = true;
        tempDataObj.conditionalField = false;
      } else {
        tempDataObj.value = false;
        tempDataObj.conditionalField = true;
      }
    } else {
      // Handle other 4-column cases
      this.handleStandardFourColumn(tempDataObj, array);
    }
  }

  private handleStandardFourColumn(tempDataObj: any, array: any[]) {
    tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
    tempDataObj.attrib_name_a = array[2].key.split('~')[0];
    tempDataObj.attrib_order_a = array[2].key.split('~')[2];

    tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
    tempDataObj.attrib_name_b = array[3].key.split('~')[0];
    tempDataObj.attrib_order_b = array[3].key.split('~')[2];

    if (array[2].value === "true") {
      tempDataObj.value = true;
    } else if (array[3].value === "true") {
      tempDataObj.value = false;
    } else {
      tempDataObj.value = 'NA';
    }

    // Assign JSON_DATA based on line number
    if (tempDataObj.num === '4a') {
      tempDataObj.JSON_DATA = this.entitiesData;
    } else if (tempDataObj.num === '4b') {
      tempDataObj.JSON_DATA = this.individualsList;
    } else if (tempDataObj.num === '5a') {
      tempDataObj.JSON_DATA = this.corporationList;
    } else {
      tempDataObj.JSON_DATA = this.otherEntityList;
    }
  }

  private handleMultiColumnData(tempDataObj: any, array: any[]) {
    tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
    tempDataObj.attrib_name_a = array[2].key.split('~')[0];
    tempDataObj.attrib_order_a = array[2].key.split('~')[2];

    tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
    tempDataObj.attrib_name_b = array[3].key.split('~')[0];
    tempDataObj.attrib_order_b = array[3].key.split('~')[2];

    if (array[2].value === "true") {
      tempDataObj.value = true;
    } else if (array[3].value === "true") {
      tempDataObj.value = false;
    } else {
      tempDataObj.value = 'NA';
    }
    
    tempDataObj.lineAttrKey_c = array[4].key.split('~')[1];
    tempDataObj.attrib_name_c = array[4].key.split('~')[0];
    tempDataObj.attrib_order_c = array[4].key.split('~')[2];
    
    if (tempDataObj.value === true) {
      if (this.isConditionalLine(tempDataObj.num)) {
        tempDataObj.value_user_input = array[4].value;
        tempDataObj.conditionalField = false;
      }
    } else {
      if (this.isConditionalLine(tempDataObj.num)) {
        tempDataObj.value_user_input = array[4].value;
        tempDataObj.conditionalField = true;
      }
    }
  }

  private isConditionalLine(num: string): boolean {
    const conditionalLines = ["3a", "3b", "3c", "7a", "7b", "7c", "13", "21", "25", "26a", "26b"];
    return conditionalLines.includes(num);
  }

  // Field visibility handler
  fieldVisibility(index: number, state: boolean, name: string, rowNum: string) {
    if (index <= 3) {
      if (state && index === 1) {
        this.setValue([2, 3], [3], true);
      } else if (state && index === 2) {
        this.setValue([1, 3], [3], true);
      } else {
        this.setValue([1, 2], [3], !this.scheduleKFormView.rows[3].conditionalField);
      }
    } else if (index === 32 || index === 41 || index === 48) {
      this.checkBoxToggle(index, name, state, [index], "value_user_input", "attrib_name2");
    } else if (rowNum === '3') {
      this.handleLine3Logic(index, state, name);
    } else if (rowNum === '7') {
      this.handleLine7Logic(index, state, name);
    } else if (rowNum === '26') {
      this.handleLine26Logic(index, state, name);
    } else {
      this.scheduleKFormView.rows[index].conditionalField = !this.scheduleKFormView.rows[index].conditionalField;
      if ((name === 'yes' && !state) || (name === 'no' && state)) {
        this.scheduleKFormView.rows[index].value = 'NA';
      }
    }
  }

  private setValue(arr: number[], toggle_i: number[], state: boolean) {
    arr.forEach((value) => {
      this.scheduleKFormView.rows[value].value = false;
    });
    toggle_i.forEach((i) => {
      this.scheduleKFormView.rows[i].conditionalField = state;
      this.scheduleKFormView.rows[i].value_user_input = '';
    });
  }

  private checkBoxToggle(index: number, name: string, state: boolean, arr: number[], val: string, attr: string) {
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleKFormView.rows[index].value = 'NA';
      if (name === 'yes' && !state) {
        arr.forEach((value) => {
          this.scheduleKFormView.rows[value].conditionalField = true;
          this.scheduleKFormView.rows[value].value_user_input = '';
        });
      }
    } else {
      this.toggleCondition(arr, state, val, attr);
    }
  }

  private toggleCondition(index: number[], state: boolean, type: string, attr: string) {
    index.forEach((value) => {
      if ((this.scheduleKFormView.rows[value].conditionalField && !state) || 
          (!this.scheduleKFormView.rows[value].conditionalField && state)) {
        return;
      } else {
        this.scheduleKFormView.rows[value].conditionalField = !this.scheduleKFormView.rows[value].conditionalField;
        this.scheduleKFormView.rows[value].value_user_input = '';
      }
    });
  }

  private handleLine3Logic(index: number, state: boolean, name: string) {
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleKFormView.rows[index].value = 'NA';
    }
    
    const line3SubLines = this.scheduleKFormView.rows.filter(o => 
      o.num === '3a' || o.num === '3b' || o.num === '3c'
    );
    
    line3SubLines.forEach((o) => {
      o.conditionalField = !state;
      if (!state) {
        o.value_user_input = '';
        o.value = '';
        if (this.einErrorList.length !== 0 && o.num === '3c') {
          this.einErrorList = this.einErrorList.filter(currentObject => 
            currentObject.num !== o.num
          );
        }
      }
    });
  }

  private handleLine7Logic(index: number, state: boolean, name: string) {
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleKFormView.rows[index].value = 'NA';
    }
    
    const line7SubLines = this.scheduleKFormView.rows.filter(o => 
      o.num === '7a' || o.num === '7b' || o.num === '7c'
    );
    
    line7SubLines.forEach((o) => {
      o.conditionalField = !state;
      if (!state) {
        o.value_user_input = '';
        o.value = '';
      }
    });
  }

  private handleLine26Logic(index: number, state: boolean, name: string) {
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleKFormView.rows[index].value = 'NA';
    }
    
    const line26SubLines = this.scheduleKFormView.rows.filter(o => 
      o.num === '26a' || o.num === '26b'
    );
    
    line26SubLines.forEach((o) => {
      o.conditionalField = !state;
      if (!state) {
        o.value_user_input = '';
        o.value = '';
      }
    });
  }

  // Update column B for line 2a dropdown
  updateColumnB(cVal: string) {
    if (typeof cVal !== "undefined" && cVal !== null) {
      this.codeMastMap.forEach((obj) => {
        if (obj.CODE_NAME === cVal) {
          this.scheduleKFormView.rows[6].value = obj.CODE_DESC;
        }
      });
    } else {
      this.scheduleKFormView.rows[6].value = '';
    }
  }

  // EIN validation
  einValidation(ein: string, line_no: string) {
    const pattern = /^\d{9}$/;
    this.validEIN = pattern.test(ein);
    
    if (!this.validEIN && (ein != null && ein !== "")) {
      const string = ein.toUpperCase();
      if (!this.einList.find(item => item.CODE_DESC === string)) {
        if (!this.einErrorList.find(item => item.num === line_no)) {
          const returnObj = {
            'num': line_no,
            'valid': this.validEIN
          };
          this.einErrorList.push(returnObj);
        }
      } else {
        this.validEIN = true;
        this.einErrorList = this.einErrorList.filter(currentObject => 
          currentObject.num !== line_no
        );
      }
    } else {
      this.einErrorList = this.einErrorList.filter(currentObject => 
        currentObject.num !== line_no
      );
    }
  }

  // Limit decimal values
  limitDecimal(val: number | string, line: string, i: number, attr: string | null) {
    // Convert input to number and handle NaN
    const numVal = Number(val);
    if (isNaN(numVal)) {
      return;
    }

    // Find the target row
    const index = this.scheduleKFormView.rows.findIndex(row => row.num === line);
    if (index === -1) {
      return;
    }

    // Handle percentage fields (7a, 7c, 26a, 26b)
    if ((line === '7a' || line === '7c' || line === '26a' || line === '26b') && attr === null) {
      // Clamp value between 0 and 100
      const clampedVal = Math.min(Math.max(numVal, 0), 100);
      this.scheduleKFormView.rows[index].value_user_input = clampedVal;
      return;
    }

    // Handle table percentage fields
    if (attr && this.scheduleKFormView.rows[index].JSON_DATA) {
      const clampedVal = Math.min(Math.max(numVal, 0), 100);
      this.scheduleKFormView.rows[index].JSON_DATA[i][attr] = clampedVal;
    }
  }

  // Add rows to sub-tables
  addRows(type: string, i: number) {
    const newRow: any = {
      OCCURRENCE: this.scheduleKFormView.rows[i].JSON_DATA.length + 1,
      DATA_ATTRIB_A: "",
      DATA_ATTRIB_B: "",
      DATA_ATTRIB_C: [this.allCountries[this.USCountry_index]],
      DATA_ATTRIB_D: ""
    };
    
    if (type === 'entity') {
      newRow.DATA_ATTRIB_C = "";
      newRow.DATA_ATTRIB_D = [this.allCountries[this.USCountry_index]];
      newRow.DATA_ATTRIB_E = "";
    }

    this.scheduleKFormView.rows[i].JSON_DATA.push(newRow);
  }

  // Remove row from sub-table
  removeRow(rowIndex: number, subTableIndex: number) {
    this.scheduleKFormView.rows[rowIndex].JSON_DATA.splice(subTableIndex, 1);
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // EIN validation
    if (this.einErrorList.length !== 0) {
      this.gtwSnackbar.openSnackBar("Please enter a valid Employer Identification Number", 'gtw-snackbar--red', true, 4);
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    // Build save data with real rowData values (like F7004 pattern)
    const tempArray: any[] = [];
    
    for (let i = 0; i < this.scheduleKFormView.rows.length; i++) {
      const row = this.scheduleKFormView.rows[i];
      
      const formRowDataA: any = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: this.getAttributeValue(row),
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };

      // Handle JSON_DATA for sub-tables
      if (this.isSubTableLine(formRowDataA.line_no) && this.shouldIncludeSubTable(i)) {
        formRowDataA.JSON_DATA = this.buildSubTableData(row, formRowDataA.line_no);
      } else {
        formRowDataA.JSON_DATA = [];
      }

      tempArray.push(formRowDataA);

      // Handle additional attributes
      if (typeof row.attrib_name_b !== "undefined") {
        const formRowDataB = this.buildSecondaryAttribute(row, i);
        tempArray.push(formRowDataB);
      }

      if (typeof row.attrib_name_c !== "undefined") {
        const formRowDataC = this.buildTertiaryAttribute(row);
        tempArray.push(formRowDataC);
      }
    }

    this.formData.editrows = [tempArray];
    this.mainData = [this.formData];

    // 🚀 CRITICAL: Pass filterParams and rowData to service (F7004 pattern)
    this.f1120SchKService.saveF1120SchKForm(
      this.GENERAL_CONFIG.base_url,  // 🚀 Use base_url (contains /gtw/)
      this.mainData,
      this.globalService,
      this.filterParams,  // 🚀 Pass dynamic filter params
      this.rowData        // 🚀 Pass row data
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string (F7004 pattern)
          this.gtwSnackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        } else {
          this.isSaveClicked = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbar.openSnackBar('Error occurred in saving the Transaction. If this continues please contact support', 'gtw-snackbar--red', true, 4);
          }
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  private getAttributeValue(row: any): string {
    const specialLines = ['2c', '3a', '3b', '3c', '7a', '7c', '26a', '26b'];
    if (specialLines.includes(row.num)) {
      return row.value_user_input || '';
    }
    
    if (row.value === true) return 'true';
    if (row.value === false) return 'false';
    if (row.value === 'NA') return 'false';
    return row.value ? row.value.toString() : '';
  }

  private isSubTableLine(lineNo: string): boolean {
    return ['5a1', '5b1', '4a1', '4b1'].includes(lineNo);
  }

  private shouldIncludeSubTable(index: number): boolean {
    return this.scheduleKFormView.rows[index - 1]?.value === true && 
           this.scheduleKFormView.rows[index - 1]?.JSON_DATA?.length > 0;
  }

  private buildSubTableData(row: any, lineNo: string): any[] {
    const attributes = lineNo === '4b1' || lineNo === '5a1' || lineNo === '5b1' ? 
      ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E'];
    const jsonData: any[] = [];

    this.scheduleKFormView.rows.find(r => r.num === row.num.replace('1', ''))?.JSON_DATA?.forEach((subRow: any, rowKey: number) => {
      const tempDataArray: any[] = [];
      let hasData = false;

      attributes.forEach((value) => {
        const index = Math.floor(jsonData.length / attributes.length) + 1;
        let tempValue;

        if (Array.isArray(subRow["DATA_ATTRIB_" + value])) {
          tempValue = subRow["DATA_ATTRIB_" + value].length ? 
            subRow["DATA_ATTRIB_" + value][0].id : "";
        } else {
          tempValue = subRow["DATA_ATTRIB_" + value];
        }

        const tempObj = {
          OCCURRENCE: index,
          ATTRIB_NAME: "DATA_ATTRIB_" + value,
          ATTRIB_VALUE: tempValue
        };

        if (tempObj.ATTRIB_VALUE !== undefined && tempObj.ATTRIB_VALUE !== "") {
          hasData = true;
        }
        tempDataArray.push(tempObj);
      });

      if (hasData) {
        tempDataArray.forEach((column) => {
          jsonData.push(column);
        });
      }
    });

    return jsonData;
  }

  private buildSecondaryAttribute(row: any, index: number): any {
    if (row.num === '1c') {
      return {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_b,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_b,
        Attribute_Name: row.attrib_name_b,
        Attribute_Value: row.value_user_input,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
    } else {
      return {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_b,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_b,
        Attribute_Name: row.attrib_name_b,
        Attribute_Value: row.value === true ? 'false' : 
                        row.value === 'NA' ? 'false' : 
                        row.value === false ? 'true' : 
                        row.value ? row.value.toString() : '',
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
    }
  }

  private buildTertiaryAttribute(row: any): any {
    return {
      tax_year: this.rowData.TAX_YEAR,
      trans_type_key: row.saveParams[1],
      line_order: row.line_order,
      attrib_order: row.attrib_order_c,
      line_description: row.description,
      combination_key: this.rowData.COMBINATION_KEY,
      group_obj_key: this.rowData.GROUP_OBJ_KEY,
      form_key: row.saveParams[2],
      line_no: row.num,
      occurence: row.saveParams[7],
      line_attrib_key: row.lineAttrKey_c,
      Attribute_Name: row.attrib_name_c,
      Attribute_Value: row.value_user_input,
      trans_dtls_key: row.saveParams[3],
      trans_status: row.saveParams[6],
      is_dirty: ''
    };
  }

  reset() {
    this.scheduleKFormView.rows = _.cloneDeep(this.originalData);
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  // Handle radio button changes (fixed checkbox issue)
  // Check if checkbox should be disabled based on 1c logic
  isCheckboxDisabled(rowNum: string): boolean {
    // Find 1c checkbox value
    const row1c = this.scheduleKFormView.rows?.find(row => row.num === '1c');
    
    if (row1c && row1c.value === true) {
      // If 1c is checked, disable 1a and 1b
      return rowNum === '1a' || rowNum === '1b';
    }
    
    return false;
  }

  // Handle checkbox change with special logic for 1a, 1b, 1c
  onCheckboxChange(rowNum: string, currentValue: boolean, rowIndex: number) {
    // Toggle the value
    const newValue = !currentValue;
    this.scheduleKFormView.rows[rowIndex].value = newValue;
    
    // Special logic for 1c
    if (rowNum === '1c' && newValue === true) {
      // When 1c is checked, uncheck and disable 1a and 1b
      this.scheduleKFormView.rows.forEach(row => {
        if (row.num === '1a' || row.num === '1b') {
          row.value = false;
        }
      });
    }
    
    // Call original field visibility logic
    this.fieldVisibility(rowIndex, newValue, '', '');
  }

onRadioChange(index: number, value: boolean, row: any) {
    row.value = value;
    this.fieldVisibility(index, value, value ? 'yes' : 'no', row.num);
  }

  // Track by function for ngFor performance
  itemTracker(index: number, item: any): any {
    return item ? item.line_attrib_key || item.LEID || index : index;
  }
}